/**
 * Choosing the paper (SRS C7).
 *
 * docs/06-UI-SPEC.md §6.5 puts this on the page tab's right-click menu. Page tabs
 * arrive at step 4.5, so until then it lives in the properties panel, where the
 * teacher can still reach every paper type.
 */

import type { BackgroundType } from '@opal/shared';

import { useBoardStore } from '../../board/store.js';

const PAPERS: readonly { readonly value: BackgroundType; readonly label: string }[] = [
  { value: 'blank', label: 'Blank' },
  { value: 'square', label: 'Square grid' },
  { value: 'dot', label: 'Dot grid' },
  { value: 'lined', label: 'Lined' },
  { value: 'graph', label: 'Graph paper' },
  { value: 'coordinate', label: 'Coordinate plane' },
  { value: 'isometric', label: 'Isometric' },
  { value: 'polar', label: 'Polar' },
];

const MIN_GRID = 10;
const MAX_GRID = 120;

export function BackgroundPicker() {
  const background = useBoardStore((state) => state.background);
  const setBackground = useBoardStore((state) => state.setBackground);

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-medium text-text-secondary">Paper</h3>
      <select
        aria-label="Paper"
        value={background.type}
        onChange={(event) => {
          setBackground({ type: event.target.value as BackgroundType });
        }}
        className="h-touch rounded-md border border-border bg-surface px-2 text-base text-text-primary"
      >
        {PAPERS.map((paper) => (
          <option key={paper.value} value={paper.value}>
            {paper.label}
          </option>
        ))}
      </select>

      <div className="flex items-center gap-3">
        <input
          type="range"
          min={MIN_GRID}
          max={MAX_GRID}
          step={5}
          value={background.gridSize}
          aria-label="Grid spacing"
          disabled={background.type === 'blank'}
          onChange={(event) => {
            setBackground({ gridSize: Number(event.target.value) });
          }}
          className="h-touch flex-1 accent-primary disabled:opacity-40"
        />
        <span className="tabular w-12 text-right text-base text-text-primary">
          {background.gridSize}
        </span>
      </div>
    </section>
  );
}
